import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarDays,
  FileText,
  User,
  MapPin,
  Briefcase,
  MessageSquare,
  ExternalLink,
  Archive,
  Bot,
} from 'lucide-react';
import { ApplicationSummary } from '../../types';
import { COLUMN_META, EMPLOYMENT_LABELS } from './kanbanConstants';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { findNextInterview } from '../../utils/applicationMetrics';

interface Props {
  application: ApplicationSummary;
  onClick: () => void;
  onArchive?: () => void;
}

const ApplicationCard: React.FC<Props> = ({ application, onClick, onArchive }) => {
  const navigate = useNavigate();
  const meta = COLUMN_META[application.status];
  const nextInterview = findNextInterview(application.scheduledInterviews);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `card:${application.id}`,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`bg-white border border-zinc-200 rounded-card border-t-2 ${meta.border} p-3.5 shadow-soft cursor-pointer select-none transition-opacity ${
        isDragging ? 'opacity-40 ring-2 ring-brand-300 shadow-elevated' : 'hover:shadow-elevated'
      }`}
    >
      <div className="flex items-start gap-2.5">
        {application.organizationLogo ? (
          <img
            src={application.organizationLogo}
            alt=""
            className="w-8 h-8 rounded-btn object-cover border border-zinc-100 shrink-0 bg-white"
          />
        ) : (
          <div className="w-8 h-8 rounded-btn bg-brand-50 flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4 text-brand-500" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] font-semibold text-foreground truncate">{application.jobTitle}</p>
            <span className="flex items-center gap-0.5 shrink-0">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.badge}`}>
                {meta.label}
              </span>
              {onArchive && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive();
                  }}
                  className="w-6 h-6 inline-flex items-center justify-center rounded-btn text-zinc-300 hover:text-brand-600 hover:bg-zinc-50 transition-colors"
                  title="Archive this application"
                  aria-label={`Archive ${application.jobTitle}`}
                >
                  <Archive className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          </div>
          <p className="text-xs text-zinc-500 truncate">{application.organizationName}</p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {application.resumeLabel && (
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
            <FileText className="w-3 h-3 text-zinc-400 shrink-0" />
            <span className="truncate">Using resume: {application.resumeLabel}</span>
          </div>
        )}
        {application.jobUrl && (
          <a
            href={/^https?:\/\//i.test(application.jobUrl) ? application.jobUrl : `https://${application.jobUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 text-[11px] text-brand-600 hover:text-brand-700 hover:underline"
          >
            <ExternalLink className="w-3 h-3 shrink-0" />
            <span className="truncate">Open job posting</span>
          </a>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/applications/${application.id}/ai-interview`);
          }}
          className="flex items-center gap-1.5 text-[11px] text-brand-600 hover:text-brand-700 hover:underline"
          title="Take an AI mock interview for this role"
        >
          <Bot className="w-3 h-3 shrink-0" />
          <span className="truncate">Practice interview</span>
        </button>
        {nextInterview && (
          <div className="flex items-center gap-1.5 text-[11px] text-indigo-600 bg-indigo-50 rounded-btn px-1.5 py-1">
            <CalendarDays className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {nextInterview.round} · {formatDateTime(nextInterview.scheduledAt)}
            </span>
          </div>
        )}
        {application.recruiterName && (
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
            <User className="w-3 h-3 text-zinc-400 shrink-0" />
            <span className="truncate">{application.recruiterName}</span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-400">
          {application.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {application.location}
            </span>
          )}
          {application.employmentType && (
            <span className="inline-flex items-center gap-1">
              <Briefcase className="w-3 h-3" /> {EMPLOYMENT_LABELS[application.employmentType]}
            </span>
          )}
          {application.appliedAt && (
            <span>Applied {formatDate(application.appliedAt)}</span>
          )}
        </div>
      </div>

      {application.interviewCount > 0 && (
        <div className="mt-2.5 flex items-center gap-1 text-[11px] text-zinc-400">
          <MessageSquare className="w-3 h-3" />
          {application.interviewCount} interview{application.interviewCount === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
};

export default ApplicationCard;